import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, Navigate, useNavigate } from "react-router-dom";
import { Home, Calendar, Award, CreditCard, User as UserIcon, ChevronDown, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { FamilyProvider, useFamily } from "@/lib/family";
import { Wordmark } from "@/components/Logo";
import ErrorBoundary from "@/components/ErrorBoundary";
import StudentChooser from "./StudentChooser";

const TABS = [
  { to: "/student/home", label: "Home", Icon: Home, end: true },
  { to: "/student/classes", label: "Classes", Icon: Calendar },
  { to: "/student/belt", label: "Belt", Icon: Award },
  { to: "/student/billing", label: "Billing", Icon: CreditCard },
  { to: "/student/profile", label: "Profile", Icon: UserIcon },
];

// Persistent family switcher: avatar + name pill in the header that opens a
// dropdown of every linked student profile. Only rendered when the parent
// has >1 linked profile.
function FamilySwitcher({ user }) {
  const { members, activeId, switchTo } = useFamily();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const nav = useNavigate();

  if (members.length <= 1) {
    return (
      <button onClick={() => nav("/student/profile")} className="flex items-center gap-2" data-testid="header-avatar">
        {user.picture
          ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          : <div className="w-8 h-8 rounded-full" style={{ background: "var(--green)" }} />}
      </button>
    );
  }

  const active = members.find(m => m.student_id === activeId) || members[0];

  const pick = async (m) => {
    setOpen(false);
    if (m.student_id === activeId) return;
    await switchTo(m.student_id);
    // Reload the shell so every page's data (reservations, billing,
    // belt, home) refetches under the newly-active profile.
    window.location.reload();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full"
        style={{ background:"var(--bg-elev)", border:"1px solid var(--border)" }}
        data-testid="family-switcher-trigger"
      >
        <div className="w-7 h-7 rounded-full flex items-center justify-center overflow-hidden shrink-0"
             style={{ background:"var(--bg-card)" }}>
          {active?.avatar
            ? <img src={active.avatar} alt="" className="w-full h-full object-cover" />
            : <span className="text-[11px] font-bold" style={{color:"var(--green)"}}>{active?.initials}</span>}
        </div>
        <span className="label-tiny uppercase max-w-[80px] truncate" style={{letterSpacing:"0.14em"}}>{active?.name?.split(" ")[0]}</span>
        <ChevronDown size={13} style={{color:"var(--text-dim)"}} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 rounded-lg overflow-hidden"
             style={{ background:"var(--bg-card)", border:"1px solid var(--border)", minWidth: 220, zIndex: 40, boxShadow:"0 10px 30px rgba(0,0,0,0.5)" }}
             data-testid="family-switcher-menu">
          <div className="px-3 py-2 flex items-center gap-2" style={{borderBottom:"1px solid var(--border)"}}>
            <Users size={12} style={{color:"var(--text-dim)"}}/>
            <span className="label-tiny" style={{color:"var(--text-dim)"}}>Switch profile</span>
          </div>
          {members.map(m => (
            <button
              key={m.student_id}
              onClick={() => pick(m)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--bg-card-hover)] text-left"
              style={{ background: m.student_id === activeId ? "var(--bg-card-hover)" : "transparent" }}
              data-testid={`family-switcher-option-${m.student_id}`}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden shrink-0"
                   style={{ background:"var(--bg-elev)" }}>
                {m.avatar
                  ? <img src={m.avatar} alt="" className="w-full h-full object-cover" />
                  : <span className="text-[11px] font-bold" style={{color:"var(--green)"}}>{m.initials}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold uppercase tracking-tight text-[12px] truncate">{m.name}</div>
                <div className="label-tiny capitalize" style={{color:"var(--text-dim)"}}>{m.belt} · {m.stripes}</div>
              </div>
              {m.student_id === activeId && <div className="w-2 h-2 rounded-full" style={{background:"var(--green)"}}/>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StudentShell() {
  const { user } = useAuth();
  const { members, activeId, loading } = useFamily();

  // Show Netflix-style chooser when the parent has >1 profile linked and
  // hasn't picked one yet on this session.
  if (!loading && members.length > 1 && !activeId) {
    return <StudentChooser />;
  }

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }} data-testid="student-shell">
      <div className="mx-auto" style={{ maxWidth: 460, paddingBottom: 96, minHeight: "100vh", borderLeft: "1px solid var(--border)", borderRight: "1px solid var(--border)" }}>
        <header className="sticky top-0 z-30 flex items-center justify-between px-5 py-4 border-b backdrop-blur"
          style={{ borderColor: "var(--border)", background: "rgba(10,11,10,0.85)" }}>
          <Wordmark size={16} />
          <FamilySwitcher user={user} />
        </header>
        <main>
          <ErrorBoundary><Outlet /></ErrorBoundary>
        </main>
      </div>

      <nav className="fixed bottom-0 left-0 right-0 border-t backdrop-blur" style={{ borderColor: "var(--border)", background: "rgba(10,11,10,0.92)", zIndex: 100 }}>
        <div className="mx-auto grid grid-cols-5" style={{ maxWidth: 480 }}>
          {TABS.map(({ to, label, Icon, end }) => (
            <NavLink key={to} to={to} end={end} className="flex flex-col items-center gap-1 py-3 transition-colors" style={({isActive}) => ({ color: isActive ? "var(--green)" : "var(--text-muted)" })} data-testid={`tab-${label.toLowerCase()}`}>
              <Icon size={20} />
              <span className="label-tiny" style={{ letterSpacing: "0.18em" }}>{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default function StudentLayout() {
  const { user, loading } = useAuth();

  if (loading) return <div style={{minHeight:"100vh",display:"grid",placeItems:"center",background:"var(--bg)"}}><div className="label">Loading…</div></div>;
  if (!user) return <Navigate to="/student/login" replace />;
  // Wrong-shell guard: an owner/coach hitting /student (e.g. PWA installed
  // while student session was active) gets sent to the owner shell.
  if (user.role === "owner" || user.role === "coach") return <Navigate to="/app" replace />;

  return (
    <FamilyProvider enabled={true}>
      <StudentShell />
    </FamilyProvider>
  );
}

export function StudentSection({ section, title, right }) {
  return (
    <div className="px-5 pt-6 pb-3 flex items-end justify-between">
      <div>
        <div className="label" style={{ color: "var(--green)" }}>{section}</div>
        <h1 className="text-[26px] font-bold tracking-tight mt-1 uppercase">{title}</h1>
      </div>
      {right}
    </div>
  );
}
